import React from 'react';
import { History } from 'history';
import moment from 'moment';

import {
  getNameOfLeashContentFieldById,
  LeashContent,
} from 'leash/utilities/localStorage/leashContentStorage';
import { LeashRoutes } from 'leash/utilities/const';
import {
  HealthDocumentTypeId,
  HealthDocumentTypeLabels,
  HealthDocumentWorkflowStatusId,
  HealthDocumentWorkflowStatusLabel,
  TravelPackagesHistory,
  TravelPackagesHistoryTableData,
} from 'leash/types';
import date from 'utilities/dates/date';
import { notify } from 'utilities/notifications/notify';
import { Facet, FacetList, Nullable, TravelPlan, TravelSequence } from 'types';
import { getLatestDocumentForType } from '../healthDocuments/healthDocuments';

const mapDisplayFacet = (
  records?: Record<string, string>[],
  keys = ['name', 'slug'],
): Facet[] => {
  return (
    records?.map((record) => ({ text: record[keys[0]], value: record[keys[1]] })) ?? []
  );
};

export const mapTravelPackages = (packages?: Nullable<TravelPlan[]>) => {
  return (
    packages?.map((travelPackage) => ({
      ...travelPackage,
      key: travelPackage.orderId,
    })) ?? []
  );
};

export const getPackageFacets = (packages?: Nullable<TravelPlan[]>): FacetList => {
  const hash = new Map();
  const facets: Record<string, Facet[]> = {
    workflowStatus: [],
    breeder: [],
    breederTier: [],
  };

  if (!packages) {
    return facets;
  }

  packages.forEach((p: TravelPlan) => {
    const { workflowStatus } = p.travelPackage;
    const breederKey = `breeder-${p.breeder.name}`;
    const workflowStatusKey = `workflowStatus-${workflowStatus}`;
    const breederTierKey = `breederTier-${p.breeder.breederTier}`;

    if (!hash.has(workflowStatusKey)) {
      hash.set(workflowStatusKey, true);
      facets.workflowStatus.push({
        text: workflowStatus ?? '',
        value: workflowStatus?.toLowerCase() ?? '',
      });
    }

    if (!hash.has(breederKey)) {
      hash.set(breederKey, true);
      facets.breeder.push({
        text: p.breeder.name,
        value: p.breeder.name.toLowerCase(),
      });
    }

    if (!hash.has(breederTierKey)) {
      hash.set(breederTierKey, true);
      if (p.breeder?.breederTier) {
        facets.breederTier.push({
          text: p.breeder.breederTier,
          value: p.breeder.breederTier.toLowerCase(),
        });
      }
    }
  });
  return {
    ...facets,
    travelTypes: mapDisplayFacet(LeashContent.travelTypes, ['name', 'id']),
    travelMethods: [
      { text: 'Unassigned', value: 'null' },
      ...mapDisplayFacet(LeashContent.travelMethods, ['name', 'id']),
    ],
    states: mapDisplayFacet(LeashContent.states, ['name', 'abbr']),
  };
};

export const filterTableMoment = (
  date: string,
  field: string | undefined,
  isAfter: boolean,
): boolean => {
  if (!field) {
    return false;
  }

  return isAfter
    ? moment(field).isSameOrAfter(moment(date), 'day')
    : moment(field).isSameOrBefore(moment(date), 'day');
};

export const preparePackagesForCsv = (packages: TravelPlan[]) => {
  return packages.map((p: TravelPlan) => {
    const { address, customer, breeder, travelPackage, puppy } = p;
    const documents = puppy.documents ?? [];
    const healthCertificate = getLatestDocumentForType(
      documents,
      HealthDocumentTypeId.HealthCertificate,
    );
    const vaccineRecord = getLatestDocumentForType(
      documents,
      HealthDocumentTypeId.VaccineRecord,
    );
    const veterinaryReport = getLatestDocumentForType(
      documents,
      HealthDocumentTypeId.VeterinaryHealthReport,
    );

    return {
      Package: travelPackage.travelType,
      Method: travelPackage.travelMethod,
      'Proposal Status': travelPackage.workflowStatus,
      'Date Sold': p.purchasedAt,

      'Contact ID': p.leadId,
      'Customer Name': `${customer.firstName} ${customer.lastName}`,
      'Customer Address': address.address,
      'Customer City': address.city,
      'Customer State': address.state,
      'Customer Zip Code': address.postalCode,
      'Customer Country': address.country,
      'Customer Email': customer.email,
      'Customer Phone': customer.phone,

      'Breeder ID': breeder.id,
      'Breeder Name': breeder.name,
      'Breeder Address': breeder.address,
      'Breeder City': breeder.city,
      'Breeder State': breeder.stateAbbr,
      'Breeder Zip Code': breeder.postalCode,
      'Breeder Country': breeder.countryAbbr,
      'Breeder Email': breeder.email,
      'Breeder Phone': breeder.phone,

      'Puppy ID': puppy.id,
      Breed: puppy.breed,
      'Date Available': puppy.dateAvailable,
      'Puppy Name': puppy.breederName,
      'Puppy Date of Birth': puppy.dateOfBirth,
      'Puppy age': moment(puppy.dateOfBirth).fromNow(true),
      'Puppy Weight': puppy.weightRange,
      'Weight Last Updated': puppy.lastWeighedAt,
      'Microchip Number': puppy.microchipRfid,
      'Departure Date': travelPackage.travelProposal?.departureTime,
      'Blackout Dates': travelPackage.blackoutDates
        ? travelPackage.blackoutDates.join(', ')
        : '',

      [HealthDocumentTypeLabels.HealthCertificate]:
        HealthDocumentWorkflowStatusLabel[
          HealthDocumentWorkflowStatusId[
            healthCertificate?.workflowEntity?.currentWorkflowStatusId ??
              HealthDocumentWorkflowStatusId.NotUploaded
          ]
        ],
      [HealthDocumentTypeLabels.VeterinaryHealthReport]:
        HealthDocumentWorkflowStatusLabel[
          HealthDocumentWorkflowStatusId[
            veterinaryReport?.workflowEntity?.currentWorkflowStatusId ??
              HealthDocumentWorkflowStatusId.NotUploaded
          ]
        ],
      [HealthDocumentTypeLabels.VaccineRecord]:
        HealthDocumentWorkflowStatusLabel[
          HealthDocumentWorkflowStatusId[
            vaccineRecord?.workflowEntity?.currentWorkflowStatusId ??
              HealthDocumentWorkflowStatusId.NotUploaded
          ]
        ],
    };
  });
};

export const reorderSequences = (
  sequences: TravelSequence[],
  startIndex: number,
  endIndex: number,
) => {
  const result = Array.from(sequences);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export const onClickOpenDetailsView = (
  travelPlan: TravelPlan,
  event: React.MouseEvent<HTMLElement, MouseEvent>,
  history: History,
) => {
  if (!travelPlan?.travelPackage?.travelMethod) {
    notify.error('Please select a travel method before proceeding');
    return;
  }

  const detailsUrl = `${LeashRoutes.Details}/${travelPlan.orderId}`;
  if (event.metaKey || event.ctrlKey) {
    window.open(detailsUrl, '_blank');
  } else {
    history.push(detailsUrl);
  }
};

export const mapTravelPackagesHistory = (
  history: TravelPackagesHistory[],
): TravelPackagesHistoryTableData[] =>
  history.map((h) => {
    const setValue = (value: Nullable<string> | number) => {
      switch (h.historyField.modelProperty) {
        case 'occurred_at':
        case 'created_at':
        case 'updated_at':
        case 'deleted_at':
          return value
            ? `${date.toUSLocale(value as string)} ${date.getTime(value as string)}`
            : 'Empty';
        case 'travel_type_id':
          return value
            ? getNameOfLeashContentFieldById('travelTypes', value as number)
            : 'Empty';
        case 'arrival_time':
        case 'departure_time':
          return value ? date.getTime(value as string) : 'Empty';
        default:
          return value || 'Empty';
      }
    };

    return {
      key: h.id,
      dateTime: `${date.toUSLocale(h.changedAt)} ${date.getTime(h.changedAt)}`,
      actionType: h.historyActionType.name,
      user: `${h.changedByUser.firstName}  ${h.changedByUser.lastName}`,
      field: h.historyField.label,
      oldValue: setValue(h.oldValue),
      newValue: setValue(h.newValue),
    };
  });

export const prepareTravelPackagesHistoryForCsv = (
  travelPackages: TravelPackagesHistoryTableData[],
) => {
  return travelPackages.map((item) => ({
    'Date & Time': item.dateTime,
    'Action Type': item.actionType,
    User: item.user,
    Field: item.field,
    'Old Value': item.oldValue,
    'New Value': item.newValue,
  }));
};
